/**
 * Source Breakdown Chart
 *
 * Donut + per-source progress bars showing how an asset's emissions
 * split across activity types (Stationary Combustion, Mobile, Fugitive,
 * Purchased Electricity, etc.). Layout mirrors `ScopeBreakdownChart` so
 * the two cards sit side-by-side without visual mismatch.
 */

import { useMemo } from 'react';
import { Layers, PieChart as PieChartIcon } from 'lucide-react';
import { PieChart, Pie, Cell, Label } from 'recharts';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { Progress } from '@g17eco/ui-modern/components/ui/progress';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@g17eco/ui-modern/components/ui/chart';
import { useEmissionsFormatter } from '../../hooks/useEmissionsFormatter';
import { ACTIVITY_TYPE_LABELS } from '../../config/activityTypeBranding';
import type { ActivityTypeBreakdown } from '../../types/emissions';

interface SourceBreakdownChartProps {
  activityTypes: ActivityTypeBreakdown[];
  totalCo2e: number;
}

/**
 * Recharts default qualitative palette for the donut slices. Picked to
 * stay distinct from the Scope donut colours (lime/emerald/teal) so the
 * two charts side-by-side don't look like restatements of each other.
 */
const SOURCE_COLORS = ['#2563eb', '#9333ea', '#ea580c', '#d97706', '#0891b2', '#be185d', '#475569', '#65a30d'];

/** Cap the legend so the card doesn't grow unbounded for assets with many activity types. */
const MAX_LEGEND_ROWS = 5;

export const SourceBreakdownChart = ({ activityTypes, totalCo2e }: SourceBreakdownChartProps) => {
  const { formatCompact } = useEmissionsFormatter();

  const chartData = useMemo(() => {
    return [...activityTypes]
      .filter((a) => a.co2e > 0)
      .sort((a, b) => b.co2e - a.co2e)
      .map((a, i) => ({
        key: a.activityType,
        label: ACTIVITY_TYPE_LABELS[a.activityType] ?? a.activityType.replace(/_/g, ' '),
        value: a.co2e,
        fill: SOURCE_COLORS[i % SOURCE_COLORS.length],
      }));
  }, [activityTypes]);

  // Group the long tail into "Other" so the legend caps at MAX_LEGEND_ROWS rows.
  const legendRows = useMemo(() => {
    if (chartData.length <= MAX_LEGEND_ROWS) return chartData;
    const head = chartData.slice(0, MAX_LEGEND_ROWS - 1);
    const tail = chartData.slice(MAX_LEGEND_ROWS - 1);
    const otherTotal = tail.reduce((sum, x) => sum + x.value, 0);
    return [
      ...head,
      {
        key: '__other',
        label: `Other (${tail.length})`,
        value: otherTotal,
        fill: '#94a3b8',
      },
    ];
  }, [chartData]);

  const chartConfig = useMemo<ChartConfig>(() => {
    const config: ChartConfig = { value: { label: 'Emissions' } };
    for (const item of chartData) {
      config[item.key] = { label: item.label, color: item.fill };
    }
    return config;
  }, [chartData]);

  const hasData = chartData.length > 0 && totalCo2e > 0;

  return (
    <Card className='tw:h-full'>
      <CardHeader>
        <div className='tw:flex tw:items-start tw:gap-2'>
          <Layers className='tw:size-4 tw:text-muted-foreground tw:mt-0.5' />
          <div>
            <CardTitle>Source Breakdown</CardTitle>
            <CardDescription>Distribution of emissions by activity type</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div className='tw:flex tw:flex-col tw:items-center tw:justify-center tw:h-[300px] tw:text-muted-foreground'>
            <PieChartIcon className='tw:size-10 tw:mb-2 tw:opacity-50' />
            <p className='tw:text-sm'>No source data</p>
          </div>
        ) : (
          <div className='tw:flex tw:items-center tw:h-[300px]'>
            <ChartContainer config={chartConfig} className='tw:w-[50%] tw:h-full'>
              <PieChart>
                <ChartTooltip content={<ChartTooltipContent hideLabel />} />
                <Pie
                  data={chartData}
                  dataKey='value'
                  nameKey='label'
                  innerRadius={68}
                  outerRadius={100}
                  strokeWidth={3}
                >
                  {chartData.map((entry) => (
                    <Cell key={entry.key} fill={entry.fill} />
                  ))}
                  <Label
                    content={({ viewBox }) => {
                      if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                        const totalFormatted = formatCompact(totalCo2e);
                        return (
                          <text x={viewBox.cx} y={viewBox.cy} textAnchor='middle' dominantBaseline='middle'>
                            <tspan x={viewBox.cx} y={viewBox.cy} className='tw:fill-foreground tw:text-2xl tw:font-bold'>
                              {totalFormatted.formatted}
                            </tspan>
                            <tspan x={viewBox.cx} y={(viewBox.cy || 0) + 20} className='tw:fill-muted-foreground tw:text-xs'>
                              {totalFormatted.unit}
                            </tspan>
                          </text>
                        );
                      }
                      return null;
                    }}
                  />
                </Pie>
              </PieChart>
            </ChartContainer>

            <div className='tw:flex-1 tw:pl-4 tw:space-y-4'>
              {legendRows.map((row) => {
                const percentage = totalCo2e > 0 ? (row.value / totalCo2e) * 100 : 0;
                return (
                  <div key={row.key}>
                    <div className='tw:flex tw:items-center tw:justify-between tw:mb-1'>
                      <div className='tw:flex tw:items-center tw:gap-2 tw:min-w-0'>
                        <span
                          className='tw:size-2.5 tw:rounded-full tw:shrink-0'
                          style={{ backgroundColor: row.fill }}
                        />
                        <span className='tw:text-sm tw:font-medium tw:truncate'>{row.label}</span>
                      </div>
                      <span className='tw:text-sm tw:font-bold tw:shrink-0 tw:ml-2'>
                        {formatCompact(row.value).display}
                      </span>
                    </div>
                    <Progress value={percentage} className='tw:h-1.5' progressColor={row.fill} />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
